import { Modal, Stack, Box, Grid, Text, Flex, Group, Badge, Button, Tooltip } from "@mantine/core";
import { IconShoppingCartDollar, IconDiscountFilled } from "@tabler/icons-react";
import { GpuListing } from "../types/GpuListing";
import { formatPrice } from "../utils/formatters";
import { getRetailerColor, getRetailerDisplayName, getStockBadgeProps } from "../utils/styleHelpers";
import { useTheme } from "../contexts/ThemeContext";

interface ListingDetailModalProps {
  listing: GpuListing | null;
  onClose: () => void;
}

export const ListingDetailModal = ({ listing, onClose }: ListingDetailModalProps) => {
  const { theme } = useTheme();
  const themeColor = theme === "green" ? "nvidiaGreen" : "amdRed";

  return (
    <Modal
      opened={listing !== null}
      onClose={onClose}
      withCloseButton={false}
      size="lg"
      centered
      padding="xl"
      transitionProps={{
        transition: "fade",
        duration: 150,
        exitDuration: 150,
      }}
      overlayProps={{
        opacity: 0.5,
        blur: 2,
      }}
      styles={{
        content: {
          background: `
            radial-gradient(circle at 20% 30%, var(--theme-overlay-20) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, var(--theme-overlay-15) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, var(--theme-overlay-12) 0%, transparent 70%),
            var(--theme-bg-modal)
          `,
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--theme-border)",
        },
        body: {
          background: "transparent",
        },
      }}
    >
      {listing && (
        <Stack gap="2xl">
          {listing.image_url && listing.image_url.trim() !== "" && (
            <Box
              style={{
                background:
                  "radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, rgba(233, 236, 239, 0.1) 40%, transparent 100%)",
                padding: "2rem",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <Box
                style={{
                  background: "white",
                  padding: "2rem",
                  borderRadius: "4px",
                  filter: "brightness(0.7) contrast(1.1)",
                }}
              >
                <img
                  src={listing.image_url}
                  alt={listing.full_name}
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "4px",
                    display: "block",
                  }}
                />
              </Box>
            </Box>
          )}

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, sm: 7 }}>
              <Tooltip label={listing.full_name} position="top">
                <Text
                  fw={500}
                  c="white"
                  className="modal-product-name"
                  style={{ cursor: "help" }}
                >
                  {listing.full_name.length > 60
                    ? listing.full_name.substring(0, 60) + "..."
                    : listing.full_name}
                </Text>
              </Tooltip>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 5 }}>
              <Flex
                justify="flex-end"
                align="center"
                gap="sm"
                style={{ height: "100%" }}
                wrap="nowrap"
              >
                {listing.original_price > 0 &&
                  listing.original_price !== listing.price && (
                    <Group gap={4} wrap="nowrap">
                      <Text size="sm" c="green" fw={500} style={{ whiteSpace: "nowrap" }}>
                        -{(listing.original_price - listing.price).toLocaleString("sv-SE")} kr
                      </Text>
                      <IconDiscountFilled
                        size={16}
                        color="var(--mantine-color-green-filled)"
                      />
                    </Group>
                  )}
                <Text size="lg" fw={700} c="white" style={{ whiteSpace: "nowrap" }}>
                  {formatPrice(listing.price)}
                </Text>
              </Flex>
            </Grid.Col>

            <Grid.Col span={8}>
              <Flex align="center" style={{ height: "100%" }}>
                <Group gap="md" className="modal-badges-desktop">
                  <Badge
                    color={getRetailerColor(listing.retailer)}
                    variant="light"
                    size="lg"
                    className="hoverable-badge-simple"
                  >
                    {getRetailerDisplayName(listing.retailer)}
                  </Badge>
                  <Badge
                    color={getStockBadgeProps(listing.stock_status).color}
                    variant="outline"
                    size="md"
                    c={getStockBadgeProps(listing.stock_status).textColor}
                  >
                    {getStockBadgeProps(listing.stock_status).text}
                  </Badge>
                </Group>
                <Group gap="xs" className="modal-badges-mobile">
                  <Badge
                    color={getRetailerColor(listing.retailer)}
                    variant="light"
                    size="sm"
                    className="hoverable-badge-simple"
                  >
                    {getRetailerDisplayName(listing.retailer)}
                  </Badge>
                  <Badge
                    color={getStockBadgeProps(listing.stock_status).color}
                    variant="outline"
                    size="sm"
                    c={getStockBadgeProps(listing.stock_status).textColor}
                  >
                    {getStockBadgeProps(listing.stock_status).text}
                  </Badge>
                </Group>
              </Flex>
            </Grid.Col>
            <Grid.Col span={4}>
              <Flex
                justify="flex-end"
                align="center"
                style={{ height: "100%" }}
              >
                {/* Desktop button */}
                <Tooltip label="View in store" position="top" className="modal-button-desktop">
                  <Button
                    component="a"
                    href={listing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    leftSection={<IconShoppingCartDollar size={20} />}
                    variant="light"
                    color={themeColor}
                  >
                    View in store
                  </Button>
                </Tooltip>
                {/* Mobile icon-only button */}
                <Tooltip label="View in store" position="top" className="modal-button-mobile">
                  <Button
                    component="a"
                    href={listing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    color={themeColor}
                    p="xs"
                  >
                    <IconShoppingCartDollar size={20} />
                  </Button>
                </Tooltip>
              </Flex>
            </Grid.Col>
          </Grid>
        </Stack>
      )}
    </Modal>
  );
};
